import { gerarUuidV4 } from '@modules/aawz-intelligence-v2/api/sseParser'
import { runDaConversa } from '@modules/aawz-intelligence-v2/utils/run-da-conversa'
import { propostaEstaAberta } from '@modules/aawz-intelligence-v2/utils/proposta-aberta'
import { recusaDeAgente, textoDaRecusa } from '@modules/aawz-intelligence-v2/utils/agentes-selecionaveis'
import { ehRecusaPorFuncionalidade } from '@modules/aawz-intelligence-v2/utils/recusa-de-funcionalidade'
import { anexosParaOCorpo } from '@modules/aawz-intelligence-v2/utils/anexos-para-o-corpo'

// [VERIFY-3b] Extraído de `view-chat.vue` (regra HARD do repo: arquivos
// ≤ 500 linhas — o arquivo foi a 862 linhas depois do VERIFY-3/VERIFY-3b).
// Todos os computeds/watchers/métodos do Modo Agente (spec-002) — a view
// fica só com o que é do Modo Chat + wiring do template. Vue mistura
// mixin+componente de forma transparente: o template de `view-chat.vue`
// continua chamando `runAtual`/`onCancelarRun`/etc. sem saber (nem precisar
// saber) que vieram daqui. Comportamento idêntico ao antes da extração —
// só corte-colagem, sem mudança de lógica.
//
// [VERIFY-3/F-3] Mesmo teto de `TIMEOUT_FALLBACK_CANCELAR_MS`
// (`apiChatModoAgente.js`) — se o `end` não chegar a tempo pelo relay, o
// fallback de lá já aborta o stream e reidrata; este watcher não precisa
// esperar mais que isso pra desistir.
const TIMEOUT_ESPERAR_RUN_TERMINAL_MS = 10000
const ESTADOS_TERMINAIS_RUN = new Set(['done', 'failed', 'cancelled', 'expired'])

const ESTADOS_DO_RUN_SEM_CHIP = Object.freeze(['done', 'expired'])

export const viewChatModoAgenteMixin = {
  computed: {
    // US-9: seletora SÓ do runtime do turno — a MESMA fonte de flags que
    // o app usa (`this.$access`, mixin global de `main.js` =
    // `store.getters['parameters/show']`), nunca um getter próprio do módulo.
    // Ligada, o envio vai por `aceitarRun` (POST /runs); desligada, Modo Chat.
    //
    // ★ Ordem do owner (2026-09-16): "quem tem home-ia deve ter
    // acesso ao ambiente inteiro do agent A". A home por IA (`home_ia`, pelo
    // MESMO helper que liga a home — inclusive o override de dev) passa a
    // ligar o Modo Agente por si; `IA - Agente` continua valendo sozinha para
    // escritório piloto sem a home. Medido antes: escritório com `home_ia` e
    // sem `IA - Agente` caía no Modo Chat ("Hub do Profissional", sem busca).
    //
    // ★ [17/09/2026] E o BACKEND tem a última palavra. Quando ele recusa o
    // aceite por funcionalidade, esta tela para de se comportar como Modo
    // Agente — ver `degradarParaModoChat`.
    /**
     * ★ [18/09/2026] O Modo Agente é OBRIGATÓRIO — ordem do owner: *"deixe o
     * modo chat também, mas force para obrigatoriamente ser só modo agente"*.
     *
     * Antes dependia de duas flags (`home_ia` ou `IA - Agente`): escritório sem
     * elas caía no Modo Chat sem nada na tela dizendo por quê. Não há mais
     * escolha — nenhuma flag liga ou desliga o modo.
     *
     * ⚠️ O caminho do Modo Chat CONTINUA no código, e de propósito: ele é a
     * queda quando o BACKEND recusa o run (`modoAgenteRecusadoPeloBackend`).
     * Sem ele, um run recusado não teria para onde ir e a mensagem morreria
     * calada. O que mudou é o significado do aviso laranja: agora ele só pode
     * querer dizer FALHA, nunca "seu escritório não tem o modo".
     */
    modoAgenteLigado() {
      return !this.modoAgenteRecusadoPeloBackend
    },
    // Modo Agente (spec-002, US-13): estado fechado do run corrente —
    // {id_run, seq, estado, motivo}. `null` fora do Modo Agente ou sem run
    // ativo — nesse caso o chip nem aparece (wireframe W2 "vazio").
    // ★ [21/09/2026] Recortado para ESTA conversa: com as abas, N views leem o
    // mesmo store fixo, e o run da aba A pintava o chip da aba B
    // (`utils/run-da-conversa.js`).
    runAtual() {
      const run = this.$store.getters['aawz-intelligence-v2/runAtual'] || null
      const conversa = this.selectedConversation
      return runDaConversa(run, conversa && conversa.id)
    },
    // Mapa por id_proposta (US-8, [adv C-25]) — nunca o singleton
    // `pendingConfirmation` sozinho, para não colidir com N propostas abertas.
    propostasAbertas() {
      return this.$store.getters['aawz-intelligence-v2/propostasAbertas'] || {}
    },
    // Só com algo a dizer ou a fazer: em curso, aguardando, falhou (tentar de novo) ou
    // cancelado com efeitos. Run já concluído/expirado não vira chip no topo da conversa.
    mostrarChipRun() {
      const run = this.runAtual
      if (!run || !run.id_run) return false
      if (ESTADOS_DO_RUN_SEM_CHIP.includes(run.estado)) return false
      if (run.estado === 'cancelled') {
        return Boolean(run.efeitos_conhecidos && run.efeitos_conhecidos.length)
      }
      return true
    },
    // ★ [A-147] O run PAROU e espera a pessoa — não é turno em andamento.
    // `chatLoading` fica `true` a espera inteira (o relay segue aberto em
    // heartbeat até a decisão, US-5/E-22), então quem olhasse só para ele
    // trataria a espera como trabalho em curso e descartaria o turno na
    // primeira desmontagem da view — levando junto as partes de tool, que é
    // de onde o CARTÃO vive. Medido em produção em 19/09/2026.
    runAguardandoDecisao() {
      return Boolean(this.runAtual && this.runAtual.estado === 'awaiting_approval')
    },
    podeCancelarRun() {
      const estado = this.runAtual && this.runAtual.estado
      return ['queued', 'running', 'awaiting_approval'].includes(estado)
    },
    // [VERIFY-2/G-D6b] Vem de `SET_RUN_CUSTO_CREDITOS` (`apiChat.getRun`),
    // não do fio SSE — `null` até a busca completar (chip não mostra nada).
    custoCreditosDoRun() {
      const valor = this.runAtual && this.runAtual.custo_creditos
      return (valor === undefined || valor === null) ? null : valor
    },
    // [1.4.9/G-D6c] Gate OBRIGATÓRIO por `retry_permitido` (evento `estado`,
    // contrato events.json) — sem ele, "Tentar de novo" apareceria pra
    // QUALQUER `failed`, mesmo quando o backend nunca autorizou o retry.
    // `retryIndisponivel` continua cobrindo o pós-409 (a última tentativa
    // dessa geração de `retry_permitido` falhou por outro motivo).
    podeTentarNovamente() {
      return Boolean(this.runAtual && this.runAtual.retry_permitido) && !this.retryIndisponivel
    },
    // [VERIFY-1/M-6] O mapa NUNCA perde entrada — `resolverProposta`/
    // `invalidarProposta` só trocam o `estado` (histórico de recibo/rejeição
    // visível nos cards, US-8). O chip anota quantas estão ABERTAS, não o
    // tamanho do mapa: sem o filtro, decidir uma e chegar outra fazia o chip
    // anunciar "2 propostas abertas" com uma só pendente.
    // [VERIFY-3h/G-D23] `propostaEstaAberta` (util compartilhado com
    // `ia-confirm-action.vue::estadoVisual`) também descarta quem expirou
    // pelo relógio local — o servidor nunca marca `estado` como 'expirada'
    // sozinho, então um filtro só pelo campo bruto contava a proposta como
    // aberta muito depois do card já mostrar "Proposta expirada.".
    quantidadePropostasAbertas() {
      return Object.values(this.propostasAbertas).filter(propostaEstaAberta).length
    },
    // W3 — banner discreto de reconexão por cursor ([adv C-30]). Getter
    // opcional: fora do Modo Agente o módulo pode nem expô-lo.
    reconectando() {
      return Boolean(this.$store.getters['aawz-intelligence-v2/reconectando'])
    },
  },
  watch: {
    // [T043-4/US-5] Reidrata o run ao trocar de conversa — cobre selecionar
    // na lateral, uma conversa nova criada pelo `start` do stream, e
    // qualquer outro caminho que troque `selectedConversation` no store.
    // Guard por id (não por referência): `updateConversationTopic` recria o
    // objeto da conversa selecionada sem trocar de conversa de verdade.
    selectedConversation: function (novaConversa, conversaAnterior) {
      const idNovo = novaConversa && novaConversa.id
      const idAnterior = conversaAnterior && conversaAnterior.id
      if (!idNovo || idNovo === idAnterior) return
      // ★ [19/09/2026] A conversa que NASCEU do nosso próprio stream não é
      // reidratada: o fio vivo é a verdade, e não há nada a recuperar. Sem
      // esta guarda, o `start` de um turno do Modo CHAT (o atalho e o
      // onboarding falam com `/chat` direto) trocava o id, a reidratação
      // achava o run já fechado daquele turno e reconectava no relay — que só
      // devolvia o `end` ~15 s depois, com a tela em "Pensando…" o tempo todo.
      // Consumida ANTES do gate do Modo Agente de propósito: a marca é de uso
      // único, e deixá-la pendurada engoliria a próxima reidratação legítima.
      //
      // ⚠️ A marca é do MOTOR que abriu o stream (`this.motor`, #2349), nunca
      // do singleton `apiChat`: com uma conversa viva, quem gravou a marca foi
      // a instância dela — perguntar ao singleton devolveria `false` e a
      // guarda deixaria de existir justamente nas conversas paralelas.
      if (this.motor.consumirConversaNascidaDoStream(idNovo)) return
      if (this.modoAgenteLigado) this.motor.reidratarRun(idNovo)
    },
    // [VERIFY-2/G-D6c] `retryIndisponivel` é por RUN — troca de conversa OU
    // um "Tentar de novo" bem-sucedido (run novo) precisa deixar o botão
    // reaparecer pro run seguinte.
    'runAtual.id_run': function () {
      this.retryIndisponivel = false
    },
    // [VERIFY-2/G-D6b] Busca `custo_creditos` só na TRANSIÇÃO pra `done`
    // (não a cada recompute — o watcher só dispara quando `estado` muda
    // de valor).
    'runAtual.estado': function (novo, antigo) {
      if (novo === 'done' && antigo !== 'done') this.buscarCustoCreditosDoRun()
    },
  },
  methods: {
    // [T043-4/US-5] Chamado por `created()` da view principal, no MESMO
    // ponto de antes da extração (ordem importa: depois de
    // `descartarConversaDeOutraPersona`, que pode ter zerado a conversa
    // selecionada) — já havia uma conversa carregada no store ao montar
    // (ex.: popup remontado sem reset do Vuex), reidrata sem esperar
    // seleção manual. O `watch: selectedConversation` acima cobre toda
    // troca DEPOIS da montagem.
    reidratarRunSeJaSelecionada() {
      if (this.modoAgenteLigado && this.selectedConversation && this.selectedConversation.id) {
        this.motor.reidratarRun(this.selectedConversation.id)
      }
    },
    /**
     * `POST /runs` (`apiChat.aceitarRun`) e o tratamento das respostas do
     * contrato (`contracts/api.yaml`): 200/`repetido:true` já conecta ao
     * relay dentro de `aceitarRun`, nada a fazer aqui. 409 (conversa ocupada)
     * reidrata `runAtual` para o chip aparecer — não reenvia nada. 402/503 e
     * o 403 de PERMISSÃO só informam.
     *
     * ★ **[17/09/2026] A recusa por FUNCIONALIDADE deixou de morrer aqui.**
     * Até esta mudança, o comentário deste método dizia que "403 NUNCA cai
     * para o Modo Chat (US-9) — indicaria `$access` desatualizado". A premissa
     * dessa regra era que as duas pontas usavam a MESMA régua; a ordem do
     * owner de 16/09 ("todo mundo que tem home-ia deve ter acesso ao ambiente
     * todo do agent A") foi cumprida só no front (`modoAgenteLigado`, acima),
     * e o backend continua exigindo `IA - Agente`
     * (`_aceitar_run_apoio.py::exigir_funcionalidade_ia_agente`). Resultado
     * MEDIDO na tela: no escritório com `home_ia` ligada e `IA - Agente`
     * desligada, TODA mensagem — digitada ou falada — entrava como bolha,
     * nunca era despachada, e o motivo saía num snackbar que some sozinho.
     *
     * Agora esse caso — e SÓ ele (`ehRecusaPorFuncionalidade`) — devolve o
     * turno ao chamador para seguir pelo Modo Chat com o MESMO texto. As
     * outras recusas continuam recusas: saldo, permissão, run duplicado e
     * ausência de worker não podem virar outro modo em silêncio.
     *
     * @returns {Promise<boolean>} `true` = o turno deve ser redespachado pelo
     *   Modo Chat; `false` = o desfecho está fechado aqui (aceito ou recusado).
     */
    async despacharMensagemModoAgente(text, conv, opcoes) {
      // ★ [#28] `opcoes` (com `anexos`/`anexosProntos`/`modoVoz`) SEMPRE foi
      // enviado pelo chamador (`view-chat.vue::despacharMensagem`) — este
      // método é que não o recebia, e os anexos morriam aqui em silêncio.
      // ★ [conversas paralelas] `this.motor` — e não o singleton `apiChat` —
      // porque cada conversa viva tem a SUA instância, com o `storeNamespace`
      // dela. Pelo singleton, o turno de uma aba escreveria no store da outra.
      const r = await this.motor.aceitarRun(this.pedidoDeAceite(text, conv, opcoes))
      if (!r) {
        this.$showSnack({
          text: 'Não foi possível enviar a mensagem. Tente novamente mais tarde.',
          color: 'error',
          button: true,
        })
        return false
      }
      if (r.status === 200) return false
      if (ehRecusaPorFuncionalidade(r)) return this.degradarParaModoChat(text)
      if (r.status === 409) {
        // ★ [19/09/2026] A bolha otimista SAI: o 409 diz que o run desta
        // conversa não foi criado — a frase está no thread como se tivesse
        // sido enviada, e não foi. Pior, a resposta que vier é da pergunta
        // ANTERIOR e apareceria logo abaixo dela, como se a respondesse.
        this.recolherBolhaOtimista(text)
        await this.tratarRunEmAndamento(conv)
        return false
      }
      // ★ [#172] ANTES do erro genérico: o backend recusa a escolha com um
      // motivo NOMEADO (400/403) em vez de cair no agente default em
      // silêncio, e é esse motivo que diz ao consultor POR QUE aquele
      // especialista não vai atender. Caindo no ramo de baixo, um 403 de
      // agente viraria "Contate o suporte" — jogando fora justamente o que o
      // backend fez questão de informar.
      if (this.avisarRecusaDeEspecialista(r)) return false
      this.mostrarErroModoAgente(r)
      return false
    },
    // `degradarParaModoChat` e `modoAgenteRecusadoPeloBackend` moram em
    // `view-chat-degradacao-de-gate.mixin.js` — mesmo componente, arquivo
    // separado pela regra HARD de 500 linhas que já criou este aqui.
    /**
     * O pedido do `POST /runs`.
     *
     * ★ [#172] `agent_slug` entra em CADA turno em que há escolha — não só no
     * primeiro: a escolha é por RUN (`runs.agent_slug`), e um turno sem o
     * campo cairia no agente default sem o consultor perceber que acabou de
     * trocar de especialista.
     *
     * E entra SÓ quando há escolha: sem ela o objeto tem exatamente as duas
     * chaves de sempre. Não é só economia — `view-chat-modo-agente.unit.js`
     * afirma o payload do contrato com `toHaveBeenCalledWith`, e um
     * `agent_slug: ''` a mais quebraria uma promessa que continua verdadeira.
     */
    pedidoDeAceite(text, conv, opcoes) {
      const pedido = {
        // ⚠️ NUNCA mande aqui um id que o servidor ainda não conhece: ao
        // contrário do `/chat` (que adota o id do cliente desde o PR #1834), o
        // `POST /runs` chama `exigir_conversa_valida_e_propria` e devolve 404
        // para id inexistente (`aceitar_run.py:156`). Conversa nova vai com
        // `null` e o servidor batiza.
        id_conversa: conv && conv.id ? conv.id : null,
        mensagem: text,
      }
      // ★ [#28] O extrato que o consultor anexou. Medido em produção
      // (19/09/2026, cliente Silvana 125759): o upload funcionava
      // (`uploadId`, status `enviado`) e a IA respondia "Não recebi nenhum
      // arquivo anexado nesta conversa" — o `POST /runs` aceita `anexos` e o
      // backend os repassa; faltava o front os passar.
      //
      // `anexosProntos` (não `anexos`): o segundo é o `File[]` cru que só
      // alimenta a bolha do usuário; quem carrega o `uploadId` que o servidor
      // entende é o primeiro — a mesma distinção que o Modo Chat já faz.
      const anexos = anexosParaOCorpo(opcoes && opcoes.anexosProntos)
      if (anexos.length) pedido.anexos = anexos
      if (this.especialistaEscolhidoSlug) pedido.agent_slug = this.especialistaEscolhidoSlug
      return pedido
    },
    async tratarRunEmAndamento(conv) {
      const idConversa = conv && conv.id
      // [T043-4/US-5] `reidratarRun` (não só `runAtual`): reidrata o chip E
      // reconecta ao relay por cursor se o run ainda não for terminal — o
      // 409 significa que ele está rodando AGORA, então quase sempre há
      // stream pra retomar.
      if (idConversa) await this.motor.reidratarRun(idConversa)
      this.$showSnack({
        text:
          'Há um run em andamento nesta conversa. Acompanhe pelo chip de estado acima.',
        color: 'error',
        button: true,
      })
    },
    /**
     * A recusa da escolha de especialista chegou? Então fala dela, e devolve
     * `true` para o chamador não mostrar também o erro genérico.
     *
     * A escolha some do seletor junto com o aviso: manter selecionado um
     * agente que o backend acabou de recusar faria o turno seguinte falhar
     * igual, e o consultor não teria como saber por quê.
     */
    avisarRecusaDeEspecialista(r) {
      const recusa = recusaDeAgente(r)
      if (!recusa) return false
      this.especialistaEscolhidoSlug = ''
      this.$showSnack({ text: textoDaRecusa(recusa.motivo), color: 'error', button: true })
      return true
    },
    mostrarErroModoAgente(r) {
      const sufixoPorStatus = {
        402: ' Contate o gestor do escritório para revisar o orçamento de IA.',
        403: ' Contate o suporte.',
        503: ' Tente novamente mais tarde.',
      }
      const sufixo = sufixoPorStatus[r.status] || ' Tente novamente mais tarde.'
      this.$showSnack({
        text: (r.message || 'Não foi possível enviar a mensagem.') + sufixo,
        color: 'error',
        button: true,
      })
    },
    // D13: `apiChat.decidirProposta(id_run, id_proposta, {decisao, hash_payload})`
    // → `POST /runs/{id_run}/propostas/{id_proposta}/decisao`. O recibo do
    // commit já executado volta no `Envelope` (E-26) — quem fecha o card nas
    // outras abas é o evento `decisao` do SSE (T041/store), não esta chamada.
    // [VERIFY-3/F-2] `decisionIdParaReusar`: quando presente (retry do
    // "Tentar novamente"), reusa o MESMO `decision_id` da tentativa
    // anterior — o backend trata como replay da mesma decisão
    // (idempotência do contrato), nunca uma decisão nova. Guardado na
    // própria entrada do mapa (`ultimaDecisao`/`ultimoDecisionId`) pra
    // `onTentarNovamente` saber o que reexecutar.
    // ★ [lote] `silencioso` e o RETORNO existem por causa de
    // `view-chat-lote.mixin.js`: no lote, quem fala é o resumo do fim (um
    // snack por proposta viraria três avisos para o mesmo gesto), e o
    // desfecho por proposta é o que monta o "gravou/não gravou". No clique
    // individual nada muda — o argumento vem `undefined` e ninguém lê o
    // retorno.
    async decidirProposta(proposta, decisao, decisionIdParaReusar, silencioso) {
      const idRun = this.runAtual && this.runAtual.id_run
      if (!idRun || !proposta || !proposta.id_proposta) {
        return { ok: false, id_proposta: (proposta && proposta.id_proposta) || null, motivo: 'run ou proposta ausente' }
      }
      const decisionId = decisionIdParaReusar || gerarUuidV4()
      // [VERIFY-1/M-2/C-81] `loading`/`erro` por proposta — sem eles, o card
      // (`ia-confirm-action.vue`) nunca alcançava o estado "enviando"
      // (spec/wireframe W1) e, pior, uma decisão que falha travava
      // `decisaoEmVoo` PARA SEMPRE: os watchers que o liberam só disparam
      // quando esses dois props mudam, e nunca eram passados por aqui.
      // `upsertProposta` faz merge raso — não apaga `estado`/`preview`/
      // `recibo` já presentes na entrada.
      this.$store.dispatch('aawz-intelligence-v2/upsertProposta', {
        id_proposta: proposta.id_proposta, loading: true, erro: '',
        ultimaDecisao: decisao, ultimoDecisionId: decisionId,
      })
      const r = await this.motor.decidirProposta(idRun, proposta.id_proposta, {
        decisao: decisao,
        hash_payload: proposta.hash_payload,
        decision_id: decisionId,
      })
      if (!r || r.status >= 400) {
        const mensagemErro = 'Não foi possível registrar a decisão. Tente novamente ou recarregue a conversa.'
        this.$store.dispatch('aawz-intelligence-v2/upsertProposta', {
          id_proposta: proposta.id_proposta, loading: false, erro: mensagemErro,
        })
        if (!silencioso) this.$showSnack({ text: mensagemErro, color: 'error', button: true })
        return { ok: false, id_proposta: proposta.id_proposta, motivo: (r && r.message) || mensagemErro }
      }
      this.$store.dispatch('aawz-intelligence-v2/upsertProposta', {
        id_proposta: proposta.id_proposta, loading: false,
      })
      return { ok: true, id_proposta: proposta.id_proposta, motivo: '' }
    },
    // `@decidir` do card estendido (`ia-confirm-action.vue`, repassado por
    // `messages.vue`): payload `{id_proposta, decisao, hash_payload}`. Resolve
    // a proposta pelo mapa do store (fonte de verdade mais atual — não confia
    // no `hash_payload` do evento) e reusa `decidirProposta`.
    onDecidirProposta(payload) {
      const idProposta = payload && payload.id_proposta
      if (!idProposta) return
      const proposta = this.propostasAbertas[idProposta]
      if (!proposta) return
      this.decidirProposta(proposta, payload.decisao)
    },
    // [VERIFY-3/F-2] "Tentar novamente" (card em `erro`) — rechama a MESMA
    // decisão (`ultimaDecisao`) com o MESMO `decision_id` (replay
    // idempotente); sem `ultimaDecisao` guardada não há o que reexecutar.
    onTentarNovamente(payload) {
      const idProposta = payload && payload.id_proposta
      if (!idProposta) return
      const proposta = this.propostasAbertas[idProposta]
      if (!proposta || !proposta.ultimaDecisao) return
      this.decidirProposta(proposta, proposta.ultimaDecisao, proposta.ultimoDecisionId)
    },
    // [VERIFY-3j/Q-10=G-D23] Fonte única de verdade: o relógio do CARD
    // (`ia-confirm-action.vue::expirouPorRelogio`) é quem sabe que expirou
    // quando o servidor nunca marcou `estado`. Grava isso no MAPA — não só
    // no card — pra `quantidadePropostasAbertas`/`propostaEstaAberta`
    // (reativas ao mapa, não ao relógio) enxergarem a mudança sem depender
    // de um tick próprio no chip.
    onExpirouPorRelogio(payload) {
      const idProposta = payload && payload.id_proposta
      if (!idProposta) return
      this.$store.dispatch('aawz-intelligence-v2/upsertProposta', {
        id_proposta: idProposta, estado: 'expirada',
      })
    },
    // Chip de estado do run (US-13) — "Cancelar" do wireframe W2.
    async onCancelarRun() {
      const idRun = this.runAtual && this.runAtual.id_run
      if (!idRun) return
      const r = await this.motor.cancelarRun(idRun)
      if (!r || r.status >= 400) {
        this.$showSnack({
          text: 'Não foi possível cancelar a execução. Tente novamente ou recarregue a conversa.',
          color: 'error',
          button: true,
        })
      }
    },
    // [VERIFY-2/G-D6b] Uma chamada a `GET /runs/{id_run}` ao entrar em
    // `done` — só assim o chip sabe `custo_creditos` (o fio SSE não carrega
    // esse campo pro Modo Agente).
    async buscarCustoCreditosDoRun() {
      const idRun = this.runAtual && this.runAtual.id_run
      if (!idRun) return
      await this.motor.getRun(idRun)
    },
    // [VERIFY-2/G-D6c] "Tentar de novo" (W2, estado `failed`) — 200: o run
    // novo já virou `runAtual` e o relay reconectou dentro de
    // `apiChat.retryRun` (mesmo tratamento de `aceitarRun`); 409 ("Run não
    // retomável."): snack com a mensagem do backend e o botão some.
    // `$showSnack` (não `$showSnackModal`) — mesmo padrão de `onCancelarRun`
    // logo acima; `$showSnackModal` é reservado a fluxo modal-critico
    // (gate `design-system:no-regression` bloqueia o uso fora dele).
    async onTentarDeNovo() {
      const idRun = this.runAtual && this.runAtual.id_run
      if (!idRun || this.tentandoNovamente) return
      this.tentandoNovamente = true
      try {
        const r = await this.motor.retryRun(idRun)
        if (!r || r.status >= 400) {
          this.retryIndisponivel = true
          this.$showSnack({
            text: (r && r.message) || 'Não foi possível tentar de novo. Tente novamente mais tarde ou contate o suporte.',
            color: 'error',
            button: true,
          })
        }
      } finally {
        this.tentandoNovamente = false
      }
    },
    // [VERIFY-2/G-D7 + VERIFY-3/F-3] Link "Pedir nova proposta" (proposta
    // `expirada`, W1) — reenvia a ÚLTIMA mensagem do usuário pela mesma
    // rota normal de envio (`enviarMensagem`). **F-3**: com um run NÃO
    // terminal, `enviarMensagem` cai no guard de `chatLoading` (que fica
    // `true` a espera INTEIRA de `awaiting_approval`, US-5/E-22) e o clique
    // virava no-op silencioso bem na janela em que o card expirado
    // aparece. Cancela o run atual e espera o terminal ANTES de reenviar.
    // [VERIFY-3f/3g, SHOULD residual review-frontend-pr2242] Se o terminal
    // NUNCA chegar (timeout de `esperarRunTerminal`), NÃO reenvia às cegas
    // — reenviar sem saber se o run anterior de fato parou arrisca duas
    // execuções concorrentes na mesma conversa. Mostra `$showSnack` (VERIFY-
    // 3h: era `$showSnackModal`, único uso divergente das outras 8 chamadas
    // deste arquivo — `$showSnackModal` é reservado a fluxo modal-crítico,
    // guidelines 108/111, e este é feedback de card, não de modal) e para
    // aqui; o consultor decide se tenta de novo.
    async onPedirNovaProposta() {
      // ⚠️ [20/09/2026] `this.ns`: a fala a reenviar é a da CONVERSA, e na
      // `/home-ia` ela mora em `iaConversa:<uuid>`. Lendo o namespace fixo
      // (vazio ali), a lista vinha vazia e o link "Pedir nova proposta" era um
      // `return` mudo. Os outros namespaces fixos deste arquivo (`runAtual`,
      // `propostasAbertas`, `reconectando`) FICAM: eles são o par do
      // `_dispatchNoStoreDoAgente` da API, e mover um lado só os desencontra.
      const mensagens = this.$store.getters[`${this.ns}/messages`] || []
      const ultimaDoUsuario = mensagens.slice().reverse().find((m) => m && m.role === 'user')
      if (!ultimaDoUsuario || !ultimaDoUsuario.content) return
      // ⚠️ [20/09/2026, review do #2342] Só cancela o run que ESTA conversa
      // acompanha: `runAtual` é do namespace fixo e vale para todas as abas
      // vivas, então cancelar sem perguntar mataria o turno da aba vizinha.
      // Sem o cancelamento, o pedido ainda vale — a fala é reenviada abaixo.
      if (this.podeCancelarRun && this.motor.acompanhaRun(this.runAtual.id_run)) {
        this.pedindoNovaProposta = true
        let motivoDeParada
        try {
          await this.motor.cancelarRun(this.runAtual.id_run)
          motivoDeParada = await this.esperarRunTerminal()
        } finally {
          this.pedindoNovaProposta = false
        }
        if (motivoDeParada === 'timeout') {
          this.$showSnack({
            text: 'Não foi possível encerrar o run; tente novamente.',
            color: 'error',
            button: true,
          })
          return
        }
      }
      this.enviarMensagem(ultimaDoUsuario.content)
    },
    // [VERIFY-3/F-3] Promise que resolve quando `runAtual.estado` chega a
    // um estado terminal (o `cancelarRun` já mantém o relay aberto até o
    // `end` chegar, follow-up T043-3) — com timeout de segurança pro caso
    // do fallback do próprio `cancelarRun` demorar mais que isso.
    // [VERIFY-3f/3g] Resolve com o MOTIVO da parada ('terminal'/'timeout')
    // em vez de `undefined` — o chamador precisa distinguir os dois pra
    // decidir se reenvia ou avisa o consultor (ver `onPedirNovaProposta`).
    esperarRunTerminal() {
      // [VERIFY-3j/C-98 r3] `await cancelarRun` (chamador) já pode ter
      // deixado `runAtual.estado` terminal ANTES desta chamada — instalar
      // o `$watch` depois disso nunca dispara (o watcher só vê MUDANÇAS
      // futuras), e o timeout de 10s mostrava um erro falso pro consultor.
      // Checa o estado ATUAL primeiro, com a mesma condição do watcher.
      const estadoAtual = this.runAtual && this.runAtual.estado
      if (!estadoAtual || ESTADOS_TERMINAIS_RUN.has(estadoAtual)) {
        return Promise.resolve('terminal')
      }
      return new Promise((resolve) => {
        let finalizado = false
        const finalizar = (motivo) => {
          if (finalizado) return
          finalizado = true
          clearTimeout(timer)
          pararDeObservar()
          resolve(motivo)
        }
        const pararDeObservar = this.$watch('runAtual.estado', (novo) => {
          if (!novo || ESTADOS_TERMINAIS_RUN.has(novo)) finalizar('terminal')
        })
        const timer = setTimeout(function() { finalizar('timeout') }, TIMEOUT_ESPERAR_RUN_TERMINAL_MS)
      })
    },
    // "Confirmar"/"Cancelar" no card de preview LEGADO (Modo Chat, ou Modo
    // Agente sem proposta do backend correspondente) — texto "Confirmo"/
    // "Cancelar" enviado direto ao modelo.
    // [VERIFY-3h, SHOULD-2 review-frontend-pr2242] Ramo morto removido: só
    // são chamados por `@confirm-action`/`@cancel-action` de `messages.vue`,
    // que só disparam do card LEGADO (`showLegacyConfirmAction`); esse card
    // só renderiza quando `!propostaAtual` (`messages.vue:111-113`) — ou
    // seja, `propostaPendente` (que exige um `id_proposta` casando com
    // `propostasAbertas`) é SEMPRE `null` neste ponto. A rota nova (com
    // proposta real) vai por `@decidir`/`onDecidirProposta`, não por aqui.
    confirmarAcao() {
      if (this.chatLoading) return
      this.$store.dispatch(
        'aawz-intelligence-v2/resolvePendingConfirmation',
        'confirmed'
      )
      this.enviarMensagem('Confirmo')
    },
    cancelarAcao() {
      // Mesma razão de confirmarAcao() acima.
      if (this.chatLoading) return
      this.$store.dispatch(
        'aawz-intelligence-v2/resolvePendingConfirmation',
        'cancelled'
      )
      this.enviarMensagem('Cancelar')
    },
  },
}
